import React from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Row,
} from 'reactstrap';

import { Field, FieldArray, change } from 'redux-form';

import { connect } from 'react-redux';

import Select from '../../../common/forms/select/Select';
import FormItem from '../../../common/forms/FormItem';
import TextInput from '../../../common/forms/input/TextInput';
import FileInput from '../../../common/forms/input/FileInput';
import DynamicForm from '../../../common/forms/DynamicForm';

import PreventiveTaskSelect from '../../../common/forms/select/PreventiveTaskSelect';

import {
  PREVENTIVE_TASK_EXECUTION_STATUS,
  RESOURCE_EXECUTION_MODE,
} from '../../../../config/constants';

import { enumToSelectOptions } from '../../../../utils/enum';

import { isRequired, validateEndDate } from '../../../../utils/validators';

import { DATE_TIME_FORMAT } from '../../../../config/locale';

import {
  generatePreventiveTaskResourceExecutionColumns,
  generatePreventiveTaskSameDateResourceColumns,
} from '../../../../config/dynamicFormFields';

import {
  clearPreventiveTaskResource,
  getPreventiveTaskHasResource,
  getPreventiveTaskResource,
} from '../../../../actions/task-schedule/PreventiveTask';

const FORM_NAME = 'PreventiveTaskExecutionForm';

const getSelectValue = (field) => {
  if (field && typeof field === 'object') {
    return field.value;
  }

  return field;
};

const buildEmptyResourceGroup = (previousGroup = null) => ({
  startedDate: previousGroup ? previousGroup.startedDate || null : null,

  completedDate: previousGroup ? previousGroup.completedDate || null : null,

  status:
    previousGroup && previousGroup.status
      ? previousGroup.status
      : PREVENTIVE_TASK_EXECUTION_STATUS.DONE,

  resources: [],
  comment: null,
});

const buildResourceInitialRows = (resourceList) => {
  if (!Array.isArray(resourceList)) {
    return [];
  }

  return resourceList
    .map((item) => {
      const companyResource = item.companyResource || item.resource || {};

      const resourceId =
        item.resourceId ||
        item.companyResourceId ||
        companyResource.id ||
        item.id;

      const resourceName =
        companyResource.name ||
        item.resourceName ||
        item.name ||
        `Recurso ${resourceId}`;

      if (resourceId === undefined || resourceId === null) {
        return null;
      }

      return {
        resourceId: {
          value: resourceId,
          label: resourceName,
        },

        startedDate: null,
        completedDate: null,

        status: PREVENTIVE_TASK_EXECUTION_STATUS.DONE,

        comment: null,
      };
    })
    .filter(Boolean);
};

const buildResourceOptions = (resourceList) => {
  const optionsById = new Map();

  buildResourceInitialRows(resourceList).forEach((row) => {
    const option = row.resourceId;

    optionsById.set(getSelectValue(option), option);
  });

  return Array.from(optionsById.values());
};

const validateMyEndDate = (endDateValue, allValues, props, fieldName) => {
  const resourceGroupMatch = String(fieldName).match(
    /resourceGroups\[(\d+)\]\.completedDate$/,
  );

  if (resourceGroupMatch) {
    const index = Number(resourceGroupMatch[1]);

    const group = allValues.resourceGroups && allValues.resourceGroups[index];

    if (!group) {
      return undefined;
    }

    return validateEndDate(endDateValue, group.startedDate);
  }

  const resourceMatch = String(fieldName).match(
    /resources\[(\d+)\]\.completedDate$/,
  );

  if (resourceMatch) {
    const index = Number(resourceMatch[1]);

    const resource = allValues.resources && allValues.resources[index];

    if (!resource) {
      return undefined;
    }

    return validateEndDate(endDateValue, resource.startedDate);
  }

  return validateEndDate(endDateValue, allValues.startedDate);
};

const validateSpentAmount = (value) => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const normalizedValue = String(value).replace(',', '.');

  const amount = Number(normalizedValue);

  if (Number.isNaN(amount)) {
    return 'Ingrese un monto válido';
  }

  if (amount < 0) {
    return 'El monto no puede ser negativo';
  }

  return undefined;
};

const validateResourcesRequired = (value) => {
  if (!Array.isArray(value) || !value.length) {
    return 'Seleccione al menos un recurso';
  }

  return undefined;
};

const getResourceIdsUsedByOtherGroups = (groups, currentGroupIndex) => {
  const usedResourceIds = new Set();

  (groups || []).forEach((group, groupIndex) => {
    if (groupIndex === currentGroupIndex) {
      return;
    }

    const selectedResources = Array.isArray(group && group.resources)
      ? group.resources
      : [];

    selectedResources.forEach((resource) => {
      const resourceId = getSelectValue(resource);

      if (resourceId !== undefined && resourceId !== null) {
        usedResourceIds.add(resourceId);
      }
    });
  });

  return usedResourceIds;
};

const ResourceExecutionGroups = ({
  fields,
  meta,
  resourceOptions,
  resourceGroups,
}) => {
  const addGroup = () => {
    const previousGroup = fields.length ? fields.get(fields.length - 1) : null;

    fields.push(buildEmptyResourceGroup(previousGroup));
  };

  return (
    <>
      {meta.submitFailed && typeof meta.error === 'string' && (
        <div className="alert alert-danger">{meta.error}</div>
      )}

      {fields.map((groupName, groupIndex) => {
        const usedResourceIds = getResourceIdsUsedByOtherGroups(
          resourceGroups,
          groupIndex,
        );
        const availableResourceOptions = resourceOptions.filter(
          (option) => !usedResourceIds.has(getSelectValue(option)),
        );

        const currentGroup = resourceGroups[groupIndex] || {};

        const selectedCount = Array.isArray(currentGroup.resources)
          ? currentGroup.resources.length
          : 0;

        return (
          <Card className="mb-3" key={groupName}>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <span>
                Grupo {groupIndex + 1}
                {selectedCount > 0
                  ? ` · ${selectedCount} recurso${
                      selectedCount === 1 ? '' : 's'
                    }`
                  : ''}
              </span>

              <Button
                type="button"
                color="danger"
                size="sm"
                outline
                onClick={() => fields.remove(groupIndex)}
              >
                <i className="fa fa-trash mr-1" />
                Eliminar
              </Button>
            </CardHeader>

            <CardBody>
              <Row>
                <Col lg="3" md="6" sm="12">
                  <FormGroup>
                    <FormItem label="Inicio" required>
                      <Field
                        name={`${groupName}.startedDate`}
                        component={TextInput}
                        type="datetime-local"
                        placeholder={DATE_TIME_FORMAT}
                        validate={[isRequired]}
                      />
                    </FormItem>
                  </FormGroup>
                </Col>

                <Col lg="3" md="6" sm="12">
                  <FormGroup>
                    <FormItem label="Culminación" required>
                      <Field
                        name={`${groupName}.completedDate`}
                        component={TextInput}
                        type="datetime-local"
                        placeholder={DATE_TIME_FORMAT}
                        validate={[isRequired, validateMyEndDate]}
                      />
                    </FormItem>
                  </FormGroup>
                </Col>

                <Col lg="3" md="6" sm="12">
                  <FormGroup>
                    <FormItem label="Estado" required>
                      <Field
                        name={`${groupName}.status`}
                        component={Select}
                        options={enumToSelectOptions(
                          PREVENTIVE_TASK_EXECUTION_STATUS,
                        )}
                        placeholder="Seleccione estado"
                        validate={[isRequired]}
                        isClearable
                      />
                    </FormItem>
                  </FormGroup>
                </Col>

                <Col lg="12" md="12" sm="12">
                  <FormGroup>
                    <FormItem label="Recursos" required>
                      <Field
                        name={`${groupName}.resources`}
                        component={Select}
                        options={availableResourceOptions}
                        placeholder="Seleccione uno o varios recursos"
                        validate={[validateResourcesRequired]}
                        isMulti
                        isClearable
                        closeMenuOnSelect={false}
                      />
                    </FormItem>
                  </FormGroup>
                </Col>

                <Col lg="12" md="12" sm="12">
                  <FormGroup>
                    <FormItem label="Comentario del grupo">
                      <Field
                        name={`${groupName}.comment`}
                        component={TextInput}
                        type="textarea"
                        maxLength={500}
                        placeholder="Comentario aplicable a estos recursos"
                      />
                    </FormItem>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        );
      })}

      <Button type="button" color="primary" outline onClick={addGroup}>
        <i className="fa fa-plus mr-1" />

        {fields.length
          ? 'Agregar grupo copiando fechas'
          : 'Agregar grupo de recursos'}
      </Button>
    </>
  );
};

ResourceExecutionGroups.propTypes = {
  fields: PropTypes.shape({
    length: PropTypes.number,
    get: PropTypes.func,
    map: PropTypes.func,
    push: PropTypes.func,
    remove: PropTypes.func,
  }).isRequired,

  meta: PropTypes.shape({
    error: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.shape({})),
    ]),
    submitFailed: PropTypes.bool,
  }).isRequired,

  resourceOptions: PropTypes.arrayOf(PropTypes.shape({})),

  resourceGroups: PropTypes.arrayOf(PropTypes.shape({})),
};

ResourceExecutionGroups.defaultProps = {
  resourceOptions: [],
  resourceGroups: [],
};

const PreventiveTaskExecutionFormFields = ({
  resourceExecutionMode,
  resourceGroups,
  hasResource,
  resourceList,
  dispatchChangeField,
  dispatchGetPreventiveTaskHasResource,
  dispatchGetPreventiveTaskResource,
  dispatchClearPreventiveTaskResource,
}) => {
  const selectedResourceExecutionMode = getSelectValue(resourceExecutionMode);

  const resourceOptions = buildResourceOptions(resourceList);

  const isGroupedMode =
    selectedResourceExecutionMode === RESOURCE_EXECUTION_MODE.GROUPED.value;

  const onPreventiveTaskChange = async (selectedTask) => {
    const preventiveTaskId = getSelectValue(selectedTask);

    dispatchChangeField(FORM_NAME, 'resources', []);

    dispatchChangeField(FORM_NAME, 'resourceGroups', []);

    dispatchChangeField(FORM_NAME, 'resourceExecutionMode', null);

    if (!preventiveTaskId) {
      dispatchClearPreventiveTaskResource();
      return;
    }

    const taskHasResource = await dispatchGetPreventiveTaskHasResource({
      preventiveTaskId,
    });

    if (!taskHasResource) {
      dispatchClearPreventiveTaskResource();
      return;
    }

    const loadedResources = await dispatchGetPreventiveTaskResource({
      preventiveTaskId,
    });

    if (!Array.isArray(loadedResources) || !loadedResources.length) {
      return;
    }

    dispatchChangeField(
      FORM_NAME,
      'resourceExecutionMode',
      RESOURCE_EXECUTION_MODE.GROUPED,
    );

    dispatchChangeField(FORM_NAME, 'resources', []);

    dispatchChangeField(FORM_NAME, 'resourceGroups', [
      buildEmptyResourceGroup(),
    ]);
  };

  const onResourceExecutionModeChange = (selectedMode) => {
    const mode = getSelectValue(selectedMode);

    if (!mode) {
      dispatchChangeField(FORM_NAME, 'resources', []);

      dispatchChangeField(FORM_NAME, 'resourceGroups', []);

      return;
    }

    if (mode === RESOURCE_EXECUTION_MODE.GROUPED.value) {
      dispatchChangeField(FORM_NAME, 'resources', []);

      if (!resourceGroups.length) {
        dispatchChangeField(FORM_NAME, 'resourceGroups', [
          buildEmptyResourceGroup(),
        ]);
      }

      return;
    }

    dispatchChangeField(FORM_NAME, 'resourceGroups', []);

    dispatchChangeField(
      FORM_NAME,
      'resources',
      buildResourceInitialRows(resourceList),
    );
  };

  const getResourceCardTitle = () => {
    if (isGroupedMode) {
      return 'Recursos agrupados por fecha';
    }

    if (
      selectedResourceExecutionMode ===
      RESOURCE_EXECUTION_MODE.RESOURCE_BASED.value
    ) {
      return 'Recursos con fecha propia';
    }

    return 'Recursos ejecutados';
  };

  return (
    <>
      <Card className="mb-3">
        <CardHeader>Datos de Ejecución</CardHeader>

        <CardBody>
          <Row>
            <Col md="12">
              <FormGroup row>
                <FormItem label="Tarea Programada" required>
                  <Field
                    name="preventiveTaskId"
                    component={PreventiveTaskSelect}
                    placeholder="Busca por código o nombre de tarea"
                    validate={[isRequired]}
                    isClearable
                    onValueChange={onPreventiveTaskChange}
                  />
                </FormItem>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            {hasResource && (
              <Col md="6">
                <FormGroup row>
                  <FormItem label="Modo de Ejecución" required>
                    <Field
                      name="resourceExecutionMode"
                      component={Select}
                      options={enumToSelectOptions(RESOURCE_EXECUTION_MODE)}
                      placeholder="Seleccione modo"
                      validate={[isRequired]}
                      isClearable
                      onValueChange={onResourceExecutionModeChange}
                    />
                  </FormItem>
                </FormGroup>
              </Col>
            )}

            <Col md="6">
              <FormGroup row>
                <FormItem label="Estado" required>
                  <Field
                    name="status"
                    component={Select}
                    options={enumToSelectOptions(
                      PREVENTIVE_TASK_EXECUTION_STATUS,
                    )}
                    placeholder="Seleccione estado"
                    validate={[isRequired]}
                    isClearable
                    isDisabled
                  />
                </FormItem>
              </FormGroup>
            </Col>
          </Row>

          {(!hasResource || !isGroupedMode) && (
            <Row>
              <Col md="4">
                <FormGroup row>
                  <FormItem label="Fecha Inicio" required>
                    <Field
                      name="startedDate"
                      component={TextInput}
                      type="datetime-local"
                      placeholder={DATE_TIME_FORMAT}
                      validate={[isRequired]}
                    />
                  </FormItem>
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup row>
                  <FormItem label="Fecha Culminación" required>
                    <Field
                      name="completedDate"
                      component={TextInput}
                      type="datetime-local"
                      placeholder={DATE_TIME_FORMAT}
                      validate={[isRequired, validateMyEndDate]}
                    />
                  </FormItem>
                </FormGroup>
              </Col>
            </Row>
          )}

          {hasResource && isGroupedMode && (
            <div className="alert alert-info">
              Las fechas generales se calcularán automáticamente usando el
              inicio más temprano y la culminación más tardía de los grupos.
            </div>
          )}

          <Row>
            <Col md="4">
              <FormGroup row>
                <FormItem label="Archivo Adjunto">
                  <Field
                    name="attachedFile"
                    component={FileInput}
                    accept=".pdf,.jpg,.jpeg,.png"
                    multiple={false}
                  />
                </FormItem>
              </FormGroup>
            </Col>

            <Col md="4">
              <FormGroup row>
                <FormItem label="Monto Gastado">
                  <Field
                    name="spentAmount"
                    component={TextInput}
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    validate={[validateSpentAmount]}
                  />
                </FormItem>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="12">
              <FormGroup row>
                <FormItem label="Comentarios">
                  <Field
                    name="comment"
                    component={TextInput}
                    type="textarea"
                    maxLength={500}
                    placeholder="Comentarios de la ejecución"
                  />
                </FormItem>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="12">
              <FormGroup row>
                <FormItem label="Observación">
                  <Field
                    name="observation"
                    component={TextInput}
                    type="textarea"
                    maxLength={500}
                    placeholder="Observación de la ejecución"
                  />
                </FormItem>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {hasResource && selectedResourceExecutionMode && (
        <Card className="mb-3">
          <CardHeader>{getResourceCardTitle()}</CardHeader>

          <CardBody>
            {isGroupedMode ? (
              <FieldArray
                name="resourceGroups"
                component={ResourceExecutionGroups}
                resourceOptions={resourceOptions}
                resourceGroups={resourceGroups}
              />
            ) : (
              <DynamicForm
                name="resources"
                columns={
                  selectedResourceExecutionMode ===
                  RESOURCE_EXECUTION_MODE.RESOURCE_BASED.value
                    ? generatePreventiveTaskResourceExecutionColumns(
                        validateMyEndDate,
                      )
                    : generatePreventiveTaskSameDateResourceColumns()
                }
              />
            )}
          </CardBody>
        </Card>
      )}
    </>
  );
};

PreventiveTaskExecutionFormFields.propTypes = {
  resourceExecutionMode: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.string,
  ]),

  resourceGroups: PropTypes.arrayOf(PropTypes.shape({})),

  hasResource: PropTypes.bool,

  resourceList: PropTypes.arrayOf(PropTypes.shape({})),

  dispatchChangeField: PropTypes.func.isRequired,

  dispatchGetPreventiveTaskHasResource: PropTypes.func.isRequired,

  dispatchGetPreventiveTaskResource: PropTypes.func.isRequired,

  dispatchClearPreventiveTaskResource: PropTypes.func.isRequired,
};

PreventiveTaskExecutionFormFields.defaultProps = {
  resourceExecutionMode: null,
  resourceGroups: [],
  hasResource: false,
  resourceList: [],
};

const mapStateToProps = ({ TaskScheduleUnit }) => ({
  hasResource: TaskScheduleUnit.PreventiveTask.get('hasResource'),

  resourceList: TaskScheduleUnit.PreventiveTask.getIn([
    'resource',
    'content',
  ]).toJS(),
});

const mapDispatchToProps = {
  dispatchChangeField: change,

  dispatchGetPreventiveTaskHasResource: getPreventiveTaskHasResource,

  dispatchGetPreventiveTaskResource: getPreventiveTaskResource,

  dispatchClearPreventiveTaskResource: clearPreventiveTaskResource,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PreventiveTaskExecutionFormFields);
